import React, { useState } from 'react';
import { useAppDispatch } from 'hooks';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box, Button, IconButton,
  InputAdornment, TextField, Typography
} from '@mui/material';

import { revertAll } from "../../../store";
import { logout } from "../../../store/thunks/authThunk";
import { changeOwnPassword } from '../../../store/thunks/usersThunk';
import { sendNotification } from "../../../ui/Toast";

interface FormData {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const ChangePassword = () => {
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    const result = await dispatch(changeOwnPassword(data));

    if (changeOwnPassword.fulfilled.match(result)) {
      sendNotification(t('notifications.passwordUpdated'), "success");
      reset();

      // Выход из аккаунта и перенаправление на страницу входа
      await dispatch(logout());
      const currentLanguage = localStorage.getItem("i18nextLng");
      localStorage.clear();
      currentLanguage && localStorage.setItem("i18nextLng", currentLanguage);
      dispatch(revertAll());
      navigate('/login');  // Переход на страницу входа
    } else {
      sendNotification(result.payload as string, "error");
    }
  };

  const newPasswordValue = watch("newPassword");

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        maxWidth: 400,
        mx: "auto",
        mt: 5,
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h5" textAlign="center" fontWeight="bold">
        {t("titles.changePassword")}
      </Typography>

      <Controller
        name="oldPassword"
        control={control}
        rules={{
          required: t("validation.requiredField") as string,
          validate: (value) =>
            value.length > 100
              ? t("validation.maxLength100") || "Не более 100 символов"
              : true,
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label={t("data.oldPassword")}
            placeholder={t("data.enterOldPassword") as string}
            type={showOldPassword ? "text" : "password"}
            fullWidth
            error={!!errors.oldPassword}
            helperText={errors.oldPassword?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowOldPassword((prev) => !prev)}
                  >
                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* Новый пароль */}
      <Controller
        name="newPassword"
        control={control}
        rules={{
          required: t("validation.requiredField") as string,
          validate: (value) => {
            if (value.length > 100) {
              return t("validation.maxLength100") || "Не более 100 символов";
            }
            if (value.length < 8) {
              return (
                t("validation.passwordMinLength") ||
                "Пароль должен быть не менее 8 символов"
              );
            }

            const specialCharRegex = /[!@#$%^&*(),.?":{}|<>_\-\\[\];'`~]/;
            if (!specialCharRegex.test(value)) {
              return (
                t("validation.passwordSpecialChar") ||
                "Добавьте хотя бы один спецсимвол"
              );
            }

            return true;
          },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label={t("data.newPassword")}
            placeholder={t("data.createPassword") as string}
            type={showNewPassword ? "text" : "password"}
            fullWidth
            error={!!errors.newPassword}
            helperText={errors.newPassword?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword((prev) => !prev)}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* Подтверждение пароля */}
      <Controller
        name="confirmPassword"
        control={control}
        rules={{
          required: t("validation.requiredField") as string,
          validate: (value) => {
            if (value.length > 100) {
              return t("validation.maxLength100") || "Не более 100 символов";
            }
            return (
              value === newPasswordValue ||
              (t("validation.passwordsDoNotMatch") as string)
            );
          },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label={t("data.repeatNewPassword")}
            placeholder={t("data.enterPasswordAgain") as string}
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Button variant="contained" color="primary" type="submit">
          {t("buttons.Save")}
        </Button>
        <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
          {t("buttons.Cancel")}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" mt={2}>
        <p>{t("password.requirementsTitle")}</p>
        <ul>
          {(
            t("password.requirements", { returnObjects: true }) as string[]
          ).map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </Typography>

      <Typography variant="body2" color="text.secondary" mt={2}>
        {t("password.afterChange")}
      </Typography>
    </Box>
  );
};
